import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ImageOverlay, MapContainer, Polygon, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import type { Bed, MapMode, Plant, PropertyMap, Zone } from '../../types';
import { BedPolygon } from './BedPolygon';
import { PlantMarker } from './PlantMarker';
import { latLngToPixel, pixelToLatLng } from '../../lib/utils';

// Fix Leaflet default icon paths in bundled environments
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ── MapEvents: delegates clicks to parent ────────────────────────────────────
function MapEvents({
  mapMode,
  mapHeight,
  onMapClick,
  onAddDrawPoint,
}: {
  mapMode: MapMode;
  mapHeight: number;
  onMapClick: (x: number, y: number) => void;
  onAddDrawPoint: (x: number, y: number) => void;
}) {
  useMapEvents({
    click(e) {
      const [x, y] = latLngToPixel(e.latlng.lat, e.latlng.lng, mapHeight);
      if (mapMode === 'add-plant') onMapClick(x, y);
      if (mapMode === 'draw-bed') onAddDrawPoint(x, y);
    },
  });
  return null;
}

// ── Drawing preview overlay ──────────────────────────────────────────────────
function DrawingOverlay({ points, mapHeight }: { points: [number, number][]; mapHeight: number }) {
  const positions = points.map(([x, y]) => pixelToLatLng(x, y, mapHeight));
  if (positions.length < 2) return null;
  return (
    <Polygon
      positions={positions}
      pathOptions={{ color: '#16a34a', fillColor: '#4ade80', fillOpacity: 0.2, dashArray: '6 3', weight: 2 }}
    />
  );
}

// ── Main map component ───────────────────────────────────────────────────────
interface GardenMapProps {
  propertyMap: PropertyMap;
  zones: Zone[];
  beds: Bed[];
  plants: Plant[];
  selectedBedId: string | null;
  selectedPlantId: string | null;
  mapMode: MapMode;
  drawingPoints: [number, number][];
  onBedClick: (bed: Bed) => void;
  onPlantClick: (plant: Plant) => void;
  onPlantDragEnd: (plant: Plant, x: number, y: number) => void;
  onMapClick: (x: number, y: number) => void;
  onAddDrawPoint: (x: number, y: number) => void;
}

export function GardenMap({
  propertyMap,
  zones,
  beds,
  plants,
  selectedBedId,
  selectedPlantId,
  mapMode,
  drawingPoints,
  onBedClick,
  onPlantClick,
  onPlantDragEnd,
  onMapClick,
  onAddDrawPoint,
}: GardenMapProps) {
  const { width, height } = propertyMap;
  const bounds = useMemo<L.LatLngBoundsLiteral>(() => [[0, 0], [height, width]], [height, width]);

  const mapImageUrl = propertyMap.image_key
    ? `/api/photos/file/${encodeURIComponent(propertyMap.image_key)}`
    : '/placeholder-map.svg';

  // Zone lookup for bed coloring
  const zoneMap = useMemo(
    () => Object.fromEntries(zones.map((z) => [z.id, z])),
    [zones],
  );

  // Update cursor based on map mode
  const mapRef = useRef<L.Map | null>(null);
  useEffect(() => {
    const container = mapRef.current?.getContainer();
    if (!container) return;
    container.style.cursor =
      mapMode === 'add-plant' ? 'crosshair' :
      mapMode === 'draw-bed'  ? 'crosshair' :
      '';
  }, [mapMode]);

  const handleMapCreated = useCallback((map: L.Map) => {
    mapRef.current = map;
  }, []);

  return (
    <MapContainer
      crs={L.CRS.Simple}
      bounds={bounds}
      style={{ width: '100%', height: '100%' }}
      minZoom={-3}
      maxZoom={3}
      zoomSnap={0.25}
      ref={handleMapCreated}
    >
      <ImageOverlay url={mapImageUrl} bounds={bounds} />

      {beds.map((bed) => (
        <BedPolygon
          key={bed.id}
          bed={bed}
          zone={zoneMap[bed.zone_id ?? '']}
          mapHeight={height}
          selected={bed.id === selectedBedId}
          onClick={() => onBedClick(bed)}
        />
      ))}

      {plants
        .filter((p) => p.x != null && p.y != null)
        .map((plant) => (
          <PlantMarker
            key={plant.id}
            plant={plant}
            mapHeight={height}
            selected={plant.id === selectedPlantId}
            onClick={() => onPlantClick(plant)}
            onDragEnd={(lat, lng) => {
              const [x, y] = latLngToPixel(lat, lng, height);
              onPlantDragEnd(plant, x, y);
            }}
          />
        ))}

      <DrawingOverlay points={drawingPoints} mapHeight={height} />

      <MapEvents
        mapMode={mapMode}
        mapHeight={height}
        onMapClick={onMapClick}
        onAddDrawPoint={onAddDrawPoint}
      />
    </MapContainer>
  );
}
